/**
 * @file Game state machine (GDD §22).
 *
 * States are independent objects registered by name. The machine owns the
 * enter/exit contract and broadcasts every transition, so the HUD and the audio
 * layer react to state changes without the states knowing they exist.
 */

import { Events } from '../EventBus.js';

export class StateMachine {
  /**
   * @param {import('../EventBus.js').EventBus} events Shared event bus.
   */
  constructor(events) {
    /** @type {import('../EventBus.js').EventBus} @private */
    this._events = events;
    /** @type {Map<string, import('./GameState.js').GameState>} @private */
    this._states = new Map();
    /** @type {import('./GameState.js').GameState | null} */
    this.current = null;
    /** @type {string} Name of the active state, empty before the first change. */
    this.currentName = '';
  }

  /**
   * @param {string} name Unique state name.
   * @param {import('./GameState.js').GameState} state State instance.
   * @returns {void}
   */
  register(name, state) {
    this._states.set(name, state);
  }

  /**
   * Switches state, running `exit` on the old one and `enter` on the new one.
   * Re-entering the current state is allowed and does run the full cycle, which
   * is what makes "restart" a plain `change('loading')`.
   *
   * @param {string} name Target state name.
   * @param {any} [payload] Passed straight to the new state's `enter`.
   * @returns {void}
   */
  change(name, payload) {
    const next = this._states.get(name);
    if (!next) throw new ReferenceError(`Unknown game state "${name}".`);

    const previous = this.currentName;
    this.current?.exit();
    this.current = next;
    this.currentName = name;
    next.enter(payload);

    this._events.emit(Events.STATE_CHANGED, { state: name, previous });
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    this.current?.update(dt);
  }

  /**
   * @param {string} name State name.
   * @returns {boolean} True when that state is active.
   */
  is(name) {
    return this.currentName === name;
  }

  /**
   * @returns {void}
   */
  dispose() {
    this.current?.exit();
    this.current = null;
    this.currentName = '';
    this._states.clear();
  }
}

/**
 * Canonical state names.
 * @readonly
 */
export const GameStates = Object.freeze({
  SPLASH: 'splash',
  MENU: 'menu',
  LOADING: 'loading',
  PLAYING: 'playing',
  PAUSED: 'paused',
  GAME_OVER: 'gameOver'
});

